import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import StripeConnectBanner from "../../components/StripeConnectBanner";
import PartnerPaymentHistory from "../../components/PartnerPaymentHistory";
import PortalDocumentHub from "../../components/PortalDocumentHub";
import DealForgeLogo, { DealForgeIcon } from "../../components/DealForgeLogo";
import OfferGenerator from "../../components/OfferGenerator";
import CommissionCalculator from "../../components/CommissionCalculator";
import {
  PortalSidebar,
  PortalEmptyState,
  QuickDeployAction,
  ViewHeader,
} from "../../components/PortalShared";
import ExchangeWidget from "../../components/ExchangeWidget";
import {
  Home,
  LogOut,
  Plus,
  DollarSign,
  FileText,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  ArrowRight,
  ChevronRight,
  Settings,
  Menu,
  X,
  Briefcase,
  AlertTriangle,
  CheckCircle,
  Building2,
  FileSearch,
  Target,
  Zap,
  Activity,
  Shield,
  Radio,
  ShoppingBag,
  Send,
  MapPin,
  FileSignature,
  CalendarDays
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SettingsProfileView({ profile, fields, onSave }) {
  const [form, setForm] = useState(profile || {});
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try { if (onSave) await onSave(form); toast.success("Profile updated"); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <ViewHeader title="Configuration" subtitle="Manage your portal profile and settings" />
      <div className="max-w-lg bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 space-y-4">
        {fields.map(({ key, label, type = 'text' }) => (
          <div key={key}>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">{label}</label>
            {type === 'textarea' ? (
              <textarea value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} rows={3} className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-[#D4AF37]/50" />
            ) : (
              <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50" />
            )}
          </div>
        ))}
        <button onClick={handleSave} disabled={saving} className="w-full py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold text-sm rounded transition-all disabled:opacity-50" data-testid="save-settings-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Realtor View Components ────────────────────────────────────────────────────

function RealtorListingsView({ listings }) {
  const statusColor = { active: 'text-[#00FF88] bg-emerald-500/10 border-emerald-500/20', pending: 'text-[#FF9F0A] bg-amber-500/10 border-amber-500/20', closed: 'text-white/40 bg-white/[0.04] border-white/[0.08]' };
  return (
    <div>
      <ViewHeader title="My Listings" subtitle="Active and pending property listings" count={listings.length} />
      {listings.length === 0 ? (
        <PortalEmptyState icon={Home} title="No Listings" desc="Add your first listing to start building your pipeline" />
      ) : (
        <div className="space-y-3">
          {listings.map((l, i) => (
            <div key={l.listing_id || i} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 flex items-start justify-between">
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{l.property_address || 'Property'}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                  <span>{l.property_type || 'Residential'}</span>
                  <span>{l.bedrooms || '--'} bd / {l.bathrooms || '--'} ba</span>
                  <span>{l.sqft ? `${l.sqft.toLocaleString()} sqft` : ''}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white font-bold">{formatCurrency(l.list_price || l.asking_price || 0)}</p>
                <span className={`text-[10px] px-2 py-0.5 border rounded uppercase font-semibold ${statusColor[l.status] || statusColor.active}`}>{l.status || 'active'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RealtorTransactionsView({ transactions }) {
  const stageColor = { prospect: 'text-blue-400', listing: 'text-indigo-400', offers: 'text-purple-400', contract: 'text-[#D4AF37]', closing: 'text-amber-400', closed: 'text-[#00FF88]' };
  return (
    <div>
      <ViewHeader title="Active Transactions" subtitle="Deals in progress across all stages" count={transactions.length} />
      {transactions.length === 0 ? (
        <PortalEmptyState icon={FileText} title="No Active Transactions" desc="Accepted offers and contracts will appear here" />
      ) : (
        <div className="space-y-3">
          {transactions.map((t, i) => (
            <div key={t.transaction_id || i} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-medium text-sm">{t.property_address || 'Transaction'}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                    <span>{t.transaction_type || 'Purchase'}</span>
                    <span>{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">{formatCurrency(t.sale_price || t.list_price || 0)}</p>
                  <span className={`text-xs font-semibold capitalize ${stageColor[t.stage] || 'text-white/40'}`}>{t.stage?.replace('_', ' ') || 'Active'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RealtorBuyersView({ transactions }) {
  const buyers = transactions.filter(t => t.buyer_id || t.buyer_name).slice(0, 20);
  return (
    <div>
      <ViewHeader title="Buyer Pipeline" subtitle="Buyers actively working with you" count={buyers.length} />
      {buyers.length === 0 ? (
        <PortalEmptyState icon={Users} title="No Buyers Yet" desc="Buyer contacts from active transactions will appear here" />
      ) : (
        <div className="space-y-2">
          {buyers.map((t) => (
            <div key={t.transaction_id || t.buyer_id || t.id} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-500/10 border border-blue-500/20 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{t.buyer_name || `Buyer — ${t.property_address}`}</p>
                  <p className="text-white/30 text-xs">{t.stage?.replace('_', ' ') || 'Active'}</p>
                </div>
              </div>
              <p className="text-white/60 text-sm">{formatCurrency(t.sale_price || t.list_price || 0)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RealtorSellersView({ listings }) {
  return (
    <div>
      <ViewHeader title="Seller Network" subtitle="Seller clients and listing leads" count={listings.length} />
      {listings.length === 0 ? (
        <PortalEmptyState icon={Building2} title="No Sellers Yet" desc="Listing clients will appear here as you add properties" />
      ) : (
        <div className="space-y-2">
          {listings.map((l) => (
            <div key={l.listing_id || l.id || l.property_address} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center">
                  <Home className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{l.property_address || 'Listed Property'}</p>
                  <p className="text-white/30 text-xs">{l.status || 'Active Listing'}</p>
                </div>
              </div>
              <p className="text-white/60 text-sm">{formatCurrency(l.list_price || 0)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RealtorOpportunitiesView({ listings, transactions }) {
  const all = [...listings, ...transactions].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 20);
  return (
    <div>
      <ViewHeader title="Deal Opportunities" subtitle="Recent deals and prospects in your market" count={all.length} />
      {all.length === 0 ? (
        <PortalEmptyState icon={Target} title="No Opportunities Yet" desc="Deals and prospects in your market will appear here" />
      ) : (
        <div className="space-y-2">
          {all.map((item) => (
            <div key={item.listing_id || item.transaction_id || item.id || item.property_address} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">{item.property_address || 'Opportunity'}</p>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-white/40">
                  <span>{item.property_type || item.transaction_type || 'Property'}</span>
                  <span>{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white font-bold">{formatCurrency(item.list_price || item.sale_price || 0)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RealtorTitleServicesView() {
  return (
    <div>
      <ViewHeader title="Title Services" subtitle="Connect with title companies and request title work" />
      <PortalEmptyState icon={FileSearch} title="Title Services Hub" desc="Request title searches, order insurance, and coordinate closings with connected title companies" action="Connect Title Company" onAction={() => toast.info("Contact a title company through the deals system")} />
    </div>
  );
}

function RealtorCommissionView({ stats, transactions }) {
  const closedDeals = transactions.filter(t => t.stage === 'closed');
  const totalCommission = closedDeals.reduce((s, t) => s + (t.commission || (t.sale_price || 0) * 0.03), 0);
  return (
    <div>
      <ViewHeader title="Commission Tracking" subtitle="Revenue from closed deals and pipeline value" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Commission', value: formatCurrency(totalCommission), color: 'text-[#D4AF37]', border: 'border-[#D4AF37]/20' },
          { label: 'Deals Closed', value: closedDeals.length, color: 'text-[#00FF88]', border: 'border-emerald-500/20' },
          { label: 'MTD Commission', value: formatCurrency(stats?.commission?.closed_mtd || 0), color: 'text-blue-400', border: 'border-blue-500/20' },
        ].map(({ label, value, color, border }) => (
          <div key={label} className={`bg-[#0A0A0A] border ${border} rounded-xl p-5 text-center`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-white/40 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
      <PartnerPaymentHistory partnerType="realtor" apiPrefix="/api/realtor" paymentTypeLabel="Commission Payments" />
    </div>
  );
}

function RealtorAnalyticsView({ stats, listings, transactions }) {
  const closed = transactions.filter(t => t.stage === 'closed').length;
  const conversion = listings.length > 0 ? Math.round((closed / listings.length) * 100) : 0;
  return (
    <div>
      <ViewHeader title="Performance Analytics" subtitle="Your real estate activity and market metrics" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Listings', value: listings.filter(l => l.status === 'active').length, icon: Home, color: 'text-[#D4AF37]' },
          { label: 'Transactions', value: transactions.length, icon: FileText, color: 'text-blue-400' },
          { label: 'Deals Closed', value: closed, icon: CheckCircle, color: 'text-[#00FF88]' },
          { label: 'Conversion', value: `${conversion}%`, icon: TrendingUp, color: 'text-purple-400' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 text-center">
            <Icon className={`w-6 h-6 mx-auto mb-2 ${color}`} />
            <p className="text-2xl font-bold text-white">{value}</p>
            <p className="text-white/30 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Execution Flow Stage ─────────────────────────────────────────────────────
const TRANSACTION_STAGES = [
  { key: 'prospect', label: 'PROSPECT', color: '#3B82F6' },
  { key: 'listing', label: 'LISTING', color: '#6366F1' },
  { key: 'offers', label: 'OFFERS', color: '#8B5CF6' },
  { key: 'contract', label: 'CONTRACT', color: '#D4AF37' },
  { key: 'closing', label: 'CLOSING', color: '#F59E0B' },
  { key: 'closed', label: 'CLOSED', color: '#10B981' },
];

// Map transaction stages
const STAGE_MAPPING = {
  'prospect': 'prospect',
  'active': 'listing',
  'pending_offers': 'offers',
  'under_contract': 'contract',
  'closing': 'closing',
  'closed': 'closed'
};

// Format currency
const formatCurrency = (value) => {
  if (!value) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// System Status Badge
function SystemStatusBadge({ status }) {
  const configs = {
    stable: { color: 'emerald', text: 'OPTIMAL' },
    alert: { color: 'amber', text: 'ATTENTION' },
    bottleneck: { color: 'red', text: 'BOTTLENECK' }
  };
  const config = configs[status] || configs.stable;
  
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 bg-${config.color}-500/[0.08] border border-${config.color}-500/20 rounded`}>
      <Shield className={`w-3 h-3 text-${config.color}-400`} />
      <span className={`text-[9px] font-semibold text-${config.color}-400 uppercase tracking-wider`}>{config.text}</span>
    </div>
  );
}

// Global Metric Display
function GlobalMetric({ label, value, trend, positive }) {
  return (
    <div className="text-center">
      <p className="text-[9px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-center gap-2 justify-center">
        <span className="text-xl font-semibold text-white tracking-tight">{value}</span>
        {trend && (
          <span className={`text-[10px] font-medium ${positive ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
            {trend}
          </span>
        )}
      </div>
    </div>
  );
}

// Execution Flow Stage
function ExecutionStage({ stage, count, volume, avgDays, isActive, onClick }) {
  const velocityIcon = avgDays < 7 ? TrendingUp : avgDays > 14 ? TrendingDown : Activity;
  const VelocityIcon = velocityIcon;
  
  return (
    <button
      onClick={onClick}
      className={`relative flex-1 p-4 border-t-2 transition-all ${
        isActive 
          ? 'bg-white/[0.03] border-t-[#D4AF37]' 
          : 'hover:bg-white/[0.02] border-t-transparent'
      }`}
      style={{ borderTopColor: isActive ? stage.color : 'transparent' }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: stage.color }}>
          {stage.label}
        </span>
        <VelocityIcon className={`w-3 h-3 ${
          avgDays < 7 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : avgDays > 14 ? 'text-[#FF3B30]' : 'text-white/40'
        }`} />
      </div>
      <div className="text-2xl font-bold text-white mb-1">{count}</div>
      <div className="text-[10px] text-white/40">
        {formatCurrency(volume)} volume
      </div>
      {avgDays > 0 && (
        <div className="text-[9px] text-white/30 mt-1">
          ~{avgDays.toFixed(1)}d avg
        </div>
      )}
    </button>
  );
}

// Transaction Row
function TransactionRow({ transaction, onClick }) {
  const stage = TRANSACTION_STAGES.find(s => s.key === transaction.executionStage) || TRANSACTION_STAGES[0];
  
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] hover:border-[#D4AF37]/30 transition-all"
    >
      {/* Stage Indicator */}
      <div 
        className="w-1 h-12 rounded-full" 
        style={{ backgroundColor: stage.color }}
      />
      
      {/* Property Info */}
      <div className="flex-1 text-left">
        <p className="text-white font-medium text-sm">{transaction.property_address}</p>
        <p className="text-white/40 text-xs mt-0.5">
          {transaction.bedrooms}bd/{transaction.bathrooms}ba • {transaction.sqft?.toLocaleString()} sqft
        </p>
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-6">
        <div className="text-right">
          <p className="text-white font-mono text-sm">{formatCurrency(transaction.asking_price)}</p>
          <p className="text-[10px] text-white/40">List Price</p>
        </div>
        <div className="text-right">
          <p className="text-[#D4AF37] font-mono text-sm">{formatCurrency(transaction.estimated_commission)}</p>
          <p className="text-[10px] text-white/40">Commission</p>
        </div>
        <div className="text-right">
          <p className={`font-mono text-sm ${transaction.daysActive > 30 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-white/70'}`}>
            {transaction.daysActive}d
          </p>
          <p className="text-[10px] text-white/40">Active</p>
        </div>
        <ChevronRight className="w-4 h-4 text-white/30" />
      </div>
    </button>
  );
}

// ─── Realtor Nav Sections ────────────────────────────────────────────────────────
const REALTOR_NAV = [
  {
    title: "Operations",
    items: [
      { icon: Target, label: "Command Center", view: "dashboard" },
      { icon: ShoppingBag, label: "Deal Opportunities", view: "opportunities" },
      { icon: FileText, label: "Active Listings", view: "listings" },
      { icon: Briefcase, label: "Transactions", view: "transactions" },
    ]
  },
  {
    title: "Network",
    items: [
      { icon: Users, label: "Buyer Pipeline", view: "buyers" },
      { icon: Building2, label: "Seller Pipeline", view: "sellers" },
      { icon: FileSearch, label: "Title Network", view: "title-services" },
    ]
  },
  {
    title: "Tools",
    items: [
      { icon: FileText, label: "Offer Generator", view: "offer-generator" },
      { icon: DollarSign, label: "Commission Calc", view: "commission-calc" },
      { icon: CalendarDays, label: "Deal Calendar", url: "/calendar?from=realtor" },
    ]
  },
  {
    title: "Intelligence",
    items: [
      { icon: DollarSign, label: "Revenue Stream", view: "commission" },
      { icon: TrendingUp, label: "Performance", view: "analytics" },
      { icon: FileSignature, label: "Documents", view: "documents" },
      { icon: Settings, label: "Configuration", view: "settings" },
    ]
  }
];

export default function RealtorCommandCenter({ user, loading: authLoading }) {
  const navigate = useNavigate();
  const [realtor, setRealtor] = useState(null);
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState('stable');
  const [isAdminViewing, setIsAdminViewing] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [notifyCounts, setNotifyCounts] = useState({});
  const [offerData, setOfferData] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Wait for auth check to complete before making decisions
    if (authLoading) {
      return; // Still checking auth, wait
    }
    
    // If admin is viewing without realtor session, show preview mode immediately
    if (user?.is_admin) {
      setIsAdminViewing(true);
      setRealtor({ name: 'Admin Preview', email: user.email, company_name: 'Admin View Mode' });
      setStats({ total_listings: 0, active_listings: 0, total_transactions: 0, pending_commission: 0 });
      setListings([]);
      setTransactions([]);
      setLoading(false);
      return;
    }
    
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes, listingsRes, transactionsRes, offersRes, notifyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtor/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/listings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/transactions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/portal-tools/offers`, { withCredentials: true }).catch(() => ({ data: { offers: [] } })),
        axios.get(`${BACKEND_URL}/api/portal-tools/notification-counts`, { withCredentials: true }).catch(() => ({ data: { counts: {} } })),
      ]);

      setRealtor(profileRes.data);
      setStats(statsRes.data);
      setIsAdminViewing(false);
      setOfferData(offersRes.data?.offers || []);
      setNotifyCounts(notifyRes.data?.counts || {});
      
      const transformedListings = (listingsRes.data || []).map(listing => ({
        ...listing,
        executionStage: listing.status === 'active' ? 'listing' : 
                        listing.offer_count > 0 ? 'offers' : 'prospect',
        daysActive: Math.floor((Date.now() - new Date(listing.created_at).getTime()) / (1000 * 60 * 60 * 24)),
        estimated_commission: (listing.asking_price || 0) * 0.025
      }));
      setListings(transformedListings);
      
      const transformedTxns = (transactionsRes.data || []).map(txn => ({
        ...txn,
        executionStage: STAGE_MAPPING[txn.stage] || 'contract',
        daysActive: Math.floor((Date.now() - new Date(txn.created_at).getTime()) / (1000 * 60 * 60 * 24))
      }));
      setTransactions(transformedTxns);

      const staleListings = transformedListings.filter(l => l.daysActive > 60).length;
      if (staleListings > 3) setSystemStatus('bottleneck');
      else if (staleListings > 0) setSystemStatus('alert');
      else setSystemStatus('stable');

    } catch (error) {
      if (user?.is_admin) {
        setIsAdminViewing(true);
        setRealtor({ name: 'Admin Preview', email: user.email, company_name: 'Admin View Mode' });
        setStats({ total_listings: 0, active_listings: 0, total_transactions: 0, pending_commission: 0 });
        setListings([]);
        setTransactions([]);
      } else if (error.response?.status === 401) {
        navigate("/realtor");
      } else {
        toast.error("Failed to load command center");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/realtor/auth/logout`, {}, { withCredentials: true });
      navigate("/realtor");
    } catch (error) {
      navigate("/realtor");
    }
  };

  // Calculate stage metrics
  const getStageMetrics = () => {
    const allItems = [...listings, ...transactions];
    return TRANSACTION_STAGES.map(stage => {
      const stageItems = allItems.filter(item => item.executionStage === stage.key);
      const volume = stageItems.reduce((sum, item) => sum + (item.asking_price || item.sale_price || 0), 0);
      const avgDays = stageItems.length > 0 
        ? stageItems.reduce((sum, item) => sum + item.daysActive, 0) / stageItems.length 
        : 0;
      return {
        ...stage,
        count: stageItems.length,
        volume,
        avgDays
      };
    });
  };

  // Global metrics
  const globalMetrics = {
    totalVolume: listings.reduce((sum, l) => sum + (l.asking_price || 0), 0) +
                 transactions.reduce((sum, t) => sum + (t.sale_price || 0), 0),
    pendingCommission: stats?.commission?.pending || 0,
    activeListings: stats?.listings?.active || 0,
    closingThisMonth: transactions.filter(t => ['contract', 'closing'].includes(t.executionStage)).length
  };

  const stageMetrics = getStageMetrics();

  // Filter items by active stage
  const filteredItems = activeStage 
    ? [...listings, ...transactions].filter(item => item.executionStage === activeStage)
    : [...listings.slice(0, 3), ...transactions.slice(0, 2)];

  // Canvas animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || listings.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    let particles = [];
    let animationId;

    const createParticle = () => {
      const stageIdx = Math.floor(Math.random() * (TRANSACTION_STAGES.length - 1));
      return {
        x: (stageIdx + 0.5) * (canvas.width / TRANSACTION_STAGES.length),
        y: canvas.height / 2 + (Math.random() - 0.5) * 30,
        targetX: (stageIdx + 1.5) * (canvas.width / TRANSACTION_STAGES.length),
        progress: 0,
        speed: 0.004 + Math.random() * 0.004,
        color: TRANSACTION_STAGES[stageIdx].color
      };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw connection lines
      TRANSACTION_STAGES.forEach((stage, idx) => {
        if (idx < TRANSACTION_STAGES.length - 1) {
          const x1 = (idx + 0.5) * (canvas.width / TRANSACTION_STAGES.length);
          const x2 = (idx + 1.5) * (canvas.width / TRANSACTION_STAGES.length);
          ctx.beginPath();
          ctx.moveTo(x1, canvas.height / 2);
          ctx.lineTo(x2, canvas.height / 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.02)';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });

      if (Math.random() < 0.02 && particles.length < 15) {
        particles.push(createParticle());
      }

      particles = particles.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;
        
        const x = p.x + (p.targetX - p.x) * p.progress;
        const y = p.y + Math.sin(p.progress * Math.PI) * 8;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.4 * (1 - p.progress);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [listings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <PortalSidebar
        navSections={REALTOR_NAV}
        activeView={activeView}
        setActiveView={setActiveView}
        homeLink="/realtor/dashboard"
        portal="Realtor"
        entity={{ company_name: realtor?.name }}
        entityInitial="R"
        entitySubtitle={realtor?.brokerage || 'Licensed Realtor'}
        accent="gold"
        onLogout={handleLogout}
        notifyCounts={notifyCounts}
        currentRole="realtor"
        userRoles={user?.extra_roles || []}
        isAdmin={user?.is_admin || false}
      />
      
      <main className="ml-16 lg:ml-64 transition-all">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left: Branding + Status */}
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">
                    Realtor <span className="text-[#D4AF37]">Command</span>
                  </h1>
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.15em]">Transaction Pipeline</p>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                    <div className="relative w-2 h-2">
                      <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                      <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                    </div>
                    <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                  </div>
                  
                  <SystemStatusBadge status={systemStatus} />
                </div>
              </div>

              {/* Center: Key Metrics */}
              <div className="flex items-center gap-8">
                <GlobalMetric 
                  label="Total Volume" 
                  value={`$${(globalMetrics.totalVolume / 1000000).toFixed(2)}M`}
                  trend="+8.2%"
                  positive
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Pending Commission" 
                  value={formatCurrency(globalMetrics.pendingCommission)}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Active Listings" 
                  value={globalMetrics.activeListings}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Closing Soon" 
                  value={globalMetrics.closingThisMonth}
                />
              </div>

              {/* Right: Time + Deploy */}
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-white/50 font-mono">
                    {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  <p className="text-lg font-mono font-bold text-white">
                    {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <Link to="/realtor/listings/new">
                  <Button className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold h-10 px-5">
                    <Plus className="w-4 h-4 mr-2" />
                    New Listing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Execution Flow Visualization */}
        <section className="border-b border-white/[0.06] relative">
          <canvas 
            ref={canvasRef} 
            className="absolute inset-0 w-full h-full pointer-events-none"
          />
          <div className="relative z-10 flex">
            {stageMetrics.map((stage, idx) => (
              <ExecutionStage
                key={stage.key}
                stage={stage}
                count={stage.count}
                volume={stage.volume}
                avgDays={stage.avgDays}
                isActive={activeStage === stage.key}
                onClick={() => setActiveStage(activeStage === stage.key ? null : stage.key)}
              />
            ))}
          </div>
        </section>

        {/* Stripe Connect Banner */}
        <div className="px-6 pt-4">
          <StripeConnectBanner 
            partnerType="realtor" 
            apiPrefix="/api/realtor"
            compact={true}
          />
        </div>

        {/* Main Content Grid */}
        <div className="p-6">
          {(() => {
            switch(activeView) {
              case 'opportunities': return <RealtorOpportunitiesView listings={listings} transactions={transactions} />;
              case 'listings': return <RealtorListingsView listings={listings} />;
              case 'transactions': return <RealtorTransactionsView transactions={transactions} />;
              case 'buyers': return <RealtorBuyersView transactions={transactions} />;
              case 'sellers': return <RealtorSellersView listings={listings} />;
              case 'title-services': return <RealtorTitleServicesView />;
              case 'commission': return <RealtorCommissionView stats={stats} transactions={transactions} />;
              case 'analytics': return <RealtorAnalyticsView stats={stats} listings={listings} transactions={transactions} />;
              case 'offer-generator': return <OfferGenerator offers={offerData} />;
              case 'commission-calc': return <CommissionCalculator />;
              case 'documents': return <PortalDocumentHub portalType="realtor" />;
              case 'settings': return (
                <SettingsProfileView
                  profile={realtor}
                  fields={[
                    { key: 'name', label: 'Full Name' },
                    { key: 'email', label: 'Email', type: 'email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'license_number', label: 'License Number' },
                    { key: 'brokerage', label: 'Brokerage' },
                    { key: 'service_area', label: 'Service Area' },
                  ]}
                />
              );
              default: return (
                <>
                  {/* Exchange Opportunities Widget — top priority */}
                  <ExchangeWidget role="realtor" user={user} className="mb-6" />

                  <div className="grid grid-cols-3 gap-6">
                    <div className="col-span-2 space-y-4">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">
                          {activeStage ? `${TRANSACTION_STAGES.find(s => s.key === activeStage)?.label} Pipeline` : 'Active Pipeline'}
                        </h2>
                        {activeStage && <button onClick={() => setActiveStage(null)} className="text-[10px] text-white/40 hover:text-white">Clear Filter</button>}
                      </div>
                      <div className="space-y-2">
                        {filteredItems.length > 0 ? (
                          filteredItems.map((item) => (
                            <TransactionRow key={item.listing_id || item.transaction_id} transaction={item} onClick={() => setActiveView('listings')} />
                          ))
                        ) : (
                          <div className="text-center py-16 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06]">
                            <FileText className="w-12 h-12 text-white/30 mx-auto mb-3" />
                            <p className="text-white/40">No items in pipeline</p>
                            <button onClick={() => toast.info("Add listings via the Listings view")} className="text-[#D4AF37] text-sm hover:underline mt-2">Add your first listing</button>
                          </div>
                        )}
                      </div>
                      {filteredItems.length > 0 && (
                        <button onClick={() => setActiveView('listings')} className="flex items-center justify-center gap-2 py-3 text-sm text-white/50 hover:text-[#D4AF37] transition-all duration-300 w-full">
                          View All Listings <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="space-y-4">
                      <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Quick Deploy</h2>
                      <div className="space-y-2">
                        <QuickDeployAction icon={Plus} label="New Listing" description="Add property to market" variant="primary" onClick={() => toast.info("Use the Listings view to manage properties")} />
                        <QuickDeployAction icon={Users} label="Add Buyer" description="Register new buyer contact" onClick={() => setActiveView('buyers')} />
                        <QuickDeployAction icon={Building2} label="Add Seller" description="Register new seller lead" onClick={() => setActiveView('sellers')} />
                        <QuickDeployAction icon={FileSearch} label="Title Services" description="Request title work" onClick={() => setActiveView('title-services')} />
                      </div>
                      {stats?.offers?.pending > 0 && (
                        <div className="bg-amber-500/[0.08] border border-amber-500/30 p-4 mt-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-[#FF9F0A] flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-[#FF9F0A] font-medium text-sm">{stats.offers.pending} Pending Offer{stats.offers.pending > 1 ? 's' : ''}</p>
                              <p className="text-white/40 text-xs mt-1">Review and respond to buyer offers</p>
                              <button onClick={() => setActiveView('transactions')} className="text-[#FF9F0A] text-xs hover:underline mt-2 inline-flex items-center gap-1">
                                Review Now <ArrowRight className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] p-4 mt-4">
                        <h3 className="text-[10px] font-semibold text-white/30 uppercase tracking-wider mb-3">Performance MTD</h3>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Commission Earned</span>
                            <span className="text-sm font-mono text-[#D4AF37]">{formatCurrency(stats?.commission?.closed_mtd || 0)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Deals Closed</span>
                            <span className="text-sm font-mono font-bold text-white">{stats?.transactions?.closed_mtd || 0}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Avg Days to Close</span>
                            <span className="text-sm font-mono font-bold text-white">{stats?.transactions?.avg_days_to_close || '--'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8">
                    <PartnerPaymentHistory partnerType="realtor" apiPrefix="/api/realtor" paymentTypeLabel="Commission Payments" />
                  </div>
                </>
              );
            }
          })()}
        </div>
      </main>
    </div>
  );
}
